import { type ReactNode, useRef } from 'react'

import { EditButton, SectionCard } from '@/components/Chat/ConfirmInfoCard'
import { Dialog, FormDialogContent } from '@/components/ui/dialog'
import { blockingChecks, type FillCheck } from '@/models/fillApplication'
import { CardChecks } from '../App/Customers/FillApplication/CardChecks'
import { FillTabTitleShownContext } from '../App/Customers/FillApplication/FillTabHeader'
import type { FillCardForm } from '../App/Customers/FillApplication/useFillCardSave'
import { ReviewSectionFooter } from './ReviewSectionFooter'

interface ReviewSectionProps {
  title: string
  /** This card's checks still standing. */
  checks: FillCheck[]
  open: boolean
  onOpen: () => void
  onClose: () => void
  /** Cancel drops what it holds and Save sends it. */
  form: FillCardForm
  /** Room for a table's rows, rather than the single column of fields every other part needs. */
  wide?: boolean
  /** What's saved, shown on the page. */
  summary: ReactNode
  /** The form behind the card, shown in a dialog while open. */
  children: ReactNode
}

/** One part of the review: a summary card whose form opens in a dialog. */
export function ReviewSection({
  title,
  checks,
  open,
  onOpen,
  onClose,
  form,
  wide,
  summary,
  children,
}: ReviewSectionProps) {
  const blocking = blockingChecks(checks)
  const cardRef = useRef<HTMLDivElement>(null)

  function cancel() {
    // A save in flight still reads the form, and its own close follows it.
    if (form.saving) return
    form.discard()
    onClose()
  }

  // Esc or a stray click outside shouldn't throw away unsaved edits; Cancel and the close button still do.
  function keepEdits(event: Event) {
    if ((form.unsaved ?? form.isDirty) || form.saving) event.preventDefault()
  }

  return (
    <>
      <div ref={cardRef}>
        <SectionCard
          title={title}
          action={<EditButton onClick={onOpen} label={`Edit ${title}`} />}
          warning={checks.length > blocking.length}
          onClick={onOpen}
        >
          <CardChecks checks={checks} />
          {summary}
        </SectionCard>
      </div>
      <Dialog open={open} onOpenChange={(next) => !next && cancel()}>
        <FormDialogContent
          title={title}
          footer={<ReviewSectionFooter form={form} onCancel={cancel} onClose={onClose} />}
          closeDisabled={form.saving}
          className={wide ? 'sm:max-w-5xl' : 'sm:max-w-lg'}
          onOpenAutoFocus={(event) => {
            // The top of the form, not its first input, so the opening doesn't start an edit.
            const card = (event.currentTarget as HTMLElement).querySelector<HTMLElement>('[data-fill-card]')
            if (!card) return
            event.preventDefault()
            card.focus()
          }}
          // Not opened from a DialogTrigger, so focus goes back to the card's Edit button by hand.
          onCloseAutoFocus={(event) => {
            event.preventDefault()
            cardRef.current?.querySelector<HTMLElement>('button')?.focus()
          }}
          onEscapeKeyDown={keepEdits}
          onInteractOutside={keepEdits}
        >
          <FillTabTitleShownContext.Provider value>{children}</FillTabTitleShownContext.Provider>
        </FormDialogContent>
      </Dialog>
    </>
  )
}
